import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  {
    id: 'welcome',
    emoji: '👋',
    title: 'Welcome to OneBotAds!',
    subtitle: 'Manage all your ad campaigns from a single place.',
    description: 'OneBotAds connects Meta, TikTok, Google, Snapchat and LinkedIn so you can create, track and automate your ads in just a few clicks.',
  },
  {
    id: 'connect',
    emoji: '🔗',
    title: 'Connect your first platform',
    subtitle: 'To launch your first campaign, link an ad account.',
    description: null,
  },
  {
    id: 'create',
    emoji: '🚀',
    title: 'Create your first campaign',
    subtitle: 'Three ways to publish — pick the one that fits you.',
    description: null,
  },
  {
    id: 'ready',
    emoji: '🎯',
    title: "You're all set!",
    subtitle: 'Here is everything you can do with OneBotAds.',
    description: null,
  },
];

const PLATFORMS = [
  { key: 'meta',     label: 'Meta Ads',   bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',   icon: '📘' },
  { key: 'tiktok',   label: 'TikTok Ads', bg: 'bg-pink-50',    border: 'border-pink-200',     text: 'text-pink-700',   icon: '🎵' },
  { key: 'google',   label: 'Google Ads', bg: 'bg-yellow-50',  border: 'border-yellow-200',   text: 'text-yellow-700', icon: '🔍' },
  { key: 'snapchat', label: 'Snapchat',   bg: 'bg-yellow-50',  border: 'border-yellow-300',   text: 'text-yellow-600', icon: '👻' },
  { key: 'linkedin', label: 'LinkedIn',   bg: 'bg-sky-50',     border: 'border-sky-200',      text: 'text-sky-700',    icon: '💼' },
  { key: 'shopify',  label: 'Shopify',    bg: 'bg-green-50',   border: 'border-green-200',    text: 'text-green-700',  icon: '🛍️' },
];

const CREATE_OPTIONS = [
  {
    icon: '🤖',
    title: 'Create with AI',
    desc: 'Describe your campaign in plain language. The AI fills in everything.',
    path: '/dashboard/create-ad-ai',
    color: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
    badge: 'Recommended',
    badgeColor: 'bg-purple-100 text-purple-700',
  },
  {
    icon: '✏️',
    title: 'Create manually',
    desc: 'Fill in the form with full control over every setting.',
    path: '/dashboard/create-ad',
    color: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100',
    badge: null,
  },
  {
    icon: '🛍️',
    title: 'From Shopify',
    desc: 'Pick a Shopify product and generate an ad in 3 clicks.',
    path: '/dashboard/shopify-ads',
    color: 'bg-green-50 border-green-200 hover:bg-green-100',
    badge: null,
  },
];

const FEATURES = [
  { icon: '📊', title: 'Statistics', desc: 'Real performance across all your platforms at a glance.' },
  { icon: '📋', title: 'Campaign Manager', desc: 'Pause or resume your campaigns directly from OneBotAds.' },
  { icon: '⚡', title: 'Automated rules', desc: 'Create rules that adjust or stop your campaigns automatically.' },
  { icon: '💳', title: 'Wallet & Billing', desc: 'Top up your wallet and manage your subscription from Billing.' },
];

const OnboardingFlow = ({ onDismiss }) => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      onDismiss();
    } else {
      setStep(s => s + 1);
    }
  };

  const handlePlatformClick = () => {
    onDismiss();
    navigate('/dashboard/connected-platforms');
  };

  const handleCreateClick = (path) => {
    onDismiss();
    navigate(path);
  };

  const handleGetStarted = () => {
    onDismiss();
    navigate('/dashboard/connected-platforms');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden">

        {/* Progress bar */}
        <div className="h-1 bg-gray-100 w-full">
          <div
            className="h-1 bg-[#221C51] transition-all duration-500"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        {/* Skip button */}
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 text-xs text-gray-400 hover:text-gray-600 font-medium px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Skip
        </button>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-2 pt-5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === step ? 'w-6 h-2 bg-[#221C51]' : i < step ? 'w-2 h-2 bg-[#221C51]/40' : 'w-2 h-2 bg-gray-200'
              }`}
            />
          ))}
        </div>

        <div className="px-8 pt-6 pb-8">

          {/* Header */}
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">{current.emoji}</div>
            <h2 className="text-2xl font-bold text-[#221C51] mb-2">{current.title}</h2>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto">{current.subtitle}</p>
            {current.description && (
              <p className="text-gray-400 text-xs mt-2 leading-relaxed max-w-sm mx-auto">{current.description}</p>
            )}
          </div>

          {/* Step-specific content */}

          {/* Step 0 — Welcome visual */}
          {step === 0 && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { icon: '🤖', label: 'Built-in AI' },
                { icon: '📱', label: '5 platforms' },
                { icon: '⚡', label: 'Auto rules' },
              ].map(item => (
                <div key={item.label} className="flex flex-col items-center gap-2 py-4 rounded-2xl bg-gray-50 border border-gray-100">
                  <span className="text-3xl">{item.icon}</span>
                  <span className="text-xs font-semibold text-[#221C51]">{item.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Step 1 — Connect platform */}
          {step === 1 && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              {PLATFORMS.map(p => (
                <button
                  key={p.key}
                  onClick={handlePlatformClick}
                  className={`flex flex-col items-center gap-2 py-4 rounded-2xl border ${p.bg} ${p.border} hover:shadow-md transition-all duration-150 cursor-pointer`}
                >
                  <span className="text-2xl">{p.icon}</span>
                  <span className={`text-xs font-semibold ${p.text}`}>{p.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Step 2 — Create options */}
          {step === 2 && (
            <div className="flex flex-col gap-3 mb-6">
              {CREATE_OPTIONS.map(opt => (
                <button
                  key={opt.path}
                  onClick={() => handleCreateClick(opt.path)}
                  className={`flex items-start gap-4 p-4 rounded-2xl border text-left transition-all duration-150 ${opt.color}`}
                >
                  <span className="text-2xl mt-0.5">{opt.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#221C51] text-sm">{opt.title}</span>
                      {opt.badge && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${opt.badgeColor}`}>
                          {opt.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                  </div>
                  <span className="text-gray-300 text-lg mt-0.5">›</span>
                </button>
              ))}
            </div>
          )}

          {/* Step 3 — Features overview */}
          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {FEATURES.map(f => (
                <div key={f.title} className="flex items-start gap-3 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                  <span className="text-2xl">{f.icon}</span>
                  <div>
                    <p className="font-semibold text-[#221C51] text-sm">{f.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              className={`text-sm font-medium px-5 py-2.5 rounded-xl transition-colors ${
                step === 0 ? 'invisible' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              ← Back
            </button>

            {isLast ? (
              <button
                onClick={handleGetStarted}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#221C51] text-white font-semibold text-sm rounded-xl hover:bg-[#2d2570] transition-colors shadow-md"
              >
                Connect a platform →
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#221C51] text-white font-semibold text-sm rounded-xl hover:bg-[#2d2570] transition-colors shadow-md"
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
